import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import WarehouseForm from '@/components/warehouse/WarehouseForm';
import WarehouseService from '@/api/warehouses.service';

interface WarehouseFormData {
  name: string;
  code: string;
  description: string;
  address: string;
  contactPerson: string;
  contactPhone: string;
  contactEmail: string;
  capacity: number | '';
  timezone: string;
  status: 'active' | 'inactive';
}

const CreateWarehouse: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const businessId = parseInt(localStorage.getItem('businessId') || '1');

  const handleSubmit = async (formData: WarehouseFormData) => {
    setIsLoading(true);
    
    try {
      // Prepare data for API
      const warehouseData = {
        name: formData.name,
        code: formData.code,
        description: formData.description || undefined,
        address: formData.address || undefined,
        contactPerson: formData.contactPerson || undefined,
        contactPhone: formData.contactPhone || undefined,
        contactEmail: formData.contactEmail || undefined,
        capacity: formData.capacity === '' ? undefined : Number(formData.capacity),
        timezone: formData.timezone,
        status: formData.status
      };

      const response = await WarehouseService.createWarehouse(businessId, warehouseData);

      if (response.data?.success) {
        toast({
          title: "Success",
          description: "Warehouse created successfully",
        });
        navigate('/warehouses');
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: response.data?.message || "Failed to create warehouse",
        });
      }
    } catch (error: any) {
      console.error('Error creating warehouse:', error);
      
      let errorMessage = "Failed to create warehouse";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/warehouses');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/warehouses')}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Warehouses
        </Button>
      </div>

      <div>
        <h1 className="text-3xl font-bold tracking-tight">Create New Warehouse</h1>
        <p className="text-muted-foreground">
          Add a new warehouse to your inventory management system
        </p>
      </div>

      {/* Form */}
      <WarehouseForm
        mode="create"
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isLoading={isLoading}
      />
    </div>
  );
};

export default CreateWarehouse; 